import { NO_DATA, groupDigits } from '@/utils/format';

/**
 * Rendering money that arrives as decimal strings, keyed by currency.
 *
 * Nothing here adds two currencies together. This system has no FX service,
 * so a combined total would be an invented rate sitting inside a debt figure
 * — and three honest numbers beat one wrong one. A record with two entries
 * renders as two lines.
 */

const SYMBOLS: Record<string, string> = {
  AMD: '֏',
  RUB: '₽',
  USD: '$',
  EUR: '€',
};

/** One amount, from its decimal string. Never parsed for arithmetic. */
export function formatAmount(value: string | null | undefined, currency = 'AMD'): string {
  if (value === null || value === undefined || value === '') return NO_DATA;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const symbol = SYMBOLS[currency] ?? currency;
  return `${groupDigits(Math.round(numeric))} ${symbol}`;
}

/** The same, keeping the sign visible — for a delta. */
export function formatSignedAmount(
  value: string | null | undefined,
  currency = 'AMD',
): string {
  if (value === null || value === undefined || value === '') return NO_DATA;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${formatAmount(value, currency)}`;
}

/** A quantity: trailing zeros trimmed, so 24.000 reads as «24». */
export function formatQuantity(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return NO_DATA;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

/**
 * Every currency in a per-currency record, as separate strings.
 *
 * An empty record means nothing is owed / nothing is held, which renders as a
 * single zero in the company's own currency rather than as an empty cell —
 * «0 ֏» is a fact, and a blank is a missing figure.
 */
export function formatByCurrency(
  totals: Record<string, string> | null | undefined,
  { signed = false, fallbackCurrency = 'AMD' } = {},
): string[] {
  const entries = Object.entries(totals ?? {});
  if (entries.length === 0) {
    return [signed ? formatSignedAmount('0', fallbackCurrency) : formatAmount('0', fallbackCurrency)];
  }
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) =>
      signed ? formatSignedAmount(amount, currency) : formatAmount(amount, currency),
    );
}

/** Whether a per-currency record holds anything at all. */
export function hasAmount(totals: Record<string, string> | null | undefined): boolean {
  return Object.values(totals ?? {}).some((value) => Number(value) !== 0);
}

/** A date string from the API, as a person reads it. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return NO_DATA;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
